import { lazy, Suspense, useRef, useState } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";
import type { PlaySceneHandle } from "~/three/faces/PlayScene";

const PlayStage = lazy(() =>
  import("~/three/PlayStage").then((m) => ({ default: m.PlayStage })),
);

export function Play() {
  const face = FACE_BY_ID.play;
  useMarkFaceVisited(face.id);
  useFaceSeo(face);
  const stageRef = useRef<PlaySceneHandle>(null);
  const [moves, setMoves] = useState(0);

  return (
    <section className="flex w-full flex-col gap-8 pb-16">
      <div className="relative h-[min(58vh,560px)] min-h-[340px] w-full overflow-hidden border-b border-jg-border/40 sm:h-[min(70vh,760px)] sm:min-h-[420px]">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-play/15 to-transparent"
            />
          }
        >
          <PlayStage ref={stageRef} onMove={setMoves} />
        </Suspense>

        <div className="pointer-events-none absolute inset-x-0 top-4 flex items-center justify-between gap-3 px-4 sm:px-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
          <span
            className="rounded-full border border-jg-border bg-jg-bg-soft/60 px-3 py-0.5 font-mono text-xs text-jg-fg-muted backdrop-blur-md"
            aria-live="polite"
          >
            <span className="text-jg-fg">{moves}</span> moves
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          <span style={{ color: face.themeColor }}>Drag</span> a face — or
          the space around it.
        </h1>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-jg-fg-muted">
          Sticker drag · twists that face · empty space spins the cube ·
          wheel zooms
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => stageRef.current?.twistRandom()}
            className="rounded-full border border-jg-border bg-jg-bg-soft/60 px-4 py-1.5 text-sm font-medium text-jg-fg transition-colors hover:border-jg-play/40"
          >
            Twist
          </button>
          <button
            type="button"
            onClick={() => stageRef.current?.scramble(8)}
            className="rounded-full px-4 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ backgroundColor: face.themeColor, color: "#0a0a0f" }}
          >
            Scramble
          </button>
          <button
            type="button"
            onClick={() => stageRef.current?.reset()}
            className="rounded-full border border-jg-border bg-jg-bg-soft/60 px-4 py-1.5 text-sm font-medium text-jg-fg transition-colors hover:border-jg-play/40"
          >
            Reset
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Featured game
            </p>
            <h2 className="mt-2 font-display text-xl font-semibold">Cubek</h2>
            <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted">
              The full Cubek on Google Play has an AI Solver, "Rhythm Hints"
              pulse-light teaching, AR Mode, and a stats tracker. The cube
              above is a stripped-down WebGL demo — same vibe, smaller scope.
            </p>
            <a
              href="https://play.google.com/store/apps/details?id=com.JGSON.Cubek"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: face.themeColor, color: "#0a0a0f" }}
            >
              Get Cubek on Play Store
              <span aria-hidden="true">↗</span>
            </a>
          </div>

          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              What we build
            </p>
            <ul className="mt-3 space-y-2 text-sm text-jg-fg-muted">
              <li>
                <span className="text-jg-fg">Mobile games:</span> Unity and
                Unreal end-to-end, from prototype through Play Store / App
                Store publishing.
              </li>
              <li>
                <span className="text-jg-fg">WebGL slices:</span> the demo
                above is a real React-Three-Fiber Rubik's cube — interactive
                3D for marketing pages and product showcases.
              </li>
              <li>
                <span className="text-jg-fg">Game adjacent UX:</span> heavy
                animation, particles, custom shaders, accessibility-aware
                kinetic typography.
              </li>
            </ul>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-jg-border bg-jg-bg-soft/30 p-5 text-sm text-jg-fg-muted">
          <strong className="block font-display text-jg-fg">
            About the demo
          </strong>
          <p className="mt-2">
            26 visible cubies with proper sticker coloring. Press a sticker
            and drag across its face — the layer rotates with your drag and
            snaps to the nearest 90° on release. Algorithm: ray-cast the
            sticker, find sibling stickers on the same face, pick the
            sibling whose screen-space direction best matches your drag,
            then the rotation axis is the sticker normal × that direction.
            Scramble chains 8 random twists; Reset snaps back to solved.
            Solve-detection celebration is the next polish pass.
          </p>
        </div>
      </div>
    </section>
  );
}
